import React, {useCallback, useEffect, useRef, useState} from 'react';
import {PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';
import {useAppContext} from 'contexts/ApplicationContext';
import {useLazyFetchStripePaymentSummaryQuery} from 'components/customer/Confirmation/store/gatewayApi';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {StripePaymentElementChangeEvent} from '@stripe/stripe-js';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    cardTypeSet,
    confirmationTokenSet,
    errorSet,
    errorsSet,
    isCardVerifiedSet,
    isLoadingPaymentInfoSet,
    selectIsCardVerified,
    selectPaymentMethod,
    selectPaymentType,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {useCountryHelper} from 'components/customer/Confirmation/Gateways/Stripe/helpers/countryHelper';
import {usePaymentContext} from 'components/customer/Confirmation/Payment';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {useSetPaymentOptions} from 'components/customer/Confirmation/Payment/helpers/useSetPaymentOptions';
import {CardType} from 'components/customer/Confirmation/Payment/entity/PaymentSession';
import {useUpdatePaymentIntent} from 'components/customer/Confirmation/helpers/updatePaymentIntent';

interface PaymentFormProps {
    connectedAccountId: string;
    paymentIntentId: string;
}

export const staticMessage =
    'An unexpected error occured, please try again later.';

export const PaymentForm = ({
    connectedAccountId,
    paymentIntentId,
}: PaymentFormProps) => {
    const dispatch = useAppDispatch();
    const htmlForm = useRef<HTMLFormElement>();
    const {job} = useJobContext() as PartialJob;
    const {userProfile, stateLabel} = useAppContext();
    const [loading, setLoading] = useState(false);

    const stripe = useStripe();
    const elements = useElements();
    const {setExtraActionHandler} = usePaymentContext();
    const {setPaymentOptions} = useSetPaymentOptions();

    const isCardVerified = useAppSelector(selectIsCardVerified);
    const paymentType = useAppSelector(selectPaymentType);
    const paymentMethod = useAppSelector(selectPaymentMethod);

    const {getCountryCode, getStateName} = useCountryHelper();
    const [fetchSummary, {isLoading}] = useLazyFetchStripePaymentSummaryQuery();
    const {updateIntent} = useUpdatePaymentIntent();

    const handlePaymentDetails = useCallback(async () => {
        if (!stripe || !elements) {
            return false;
        }

        setLoading(true);
        dispatch(errorsSet([]));

        const {error: submitError} = await elements.submit();

        if (submitError) {
            dispatch(isCardVerifiedSet(false));
            dispatch(errorSet(submitError.message));
            setLoading(false);
            return;
        }

        const stateName = await getStateName(
            userProfile.countryName,
            String(userProfile.addressState)
        );

        if (!stateName) {
            dispatch(isCardVerifiedSet(false));
            dispatch(errorSet(staticMessage));
            setLoading(false);
            return;
        }

        const {error, confirmationToken} = await stripe.createConfirmationToken(
            {
                elements,
                params: {
                    payment_method_data: {
                        billing_details: {
                            name: userProfile.name,
                            email: userProfile.email,
                            phone: userProfile.phNumber,
                            address: {
                                // prolly make this more dynamic for more countries later
                                country: getCountryCode(
                                    userProfile.countryName
                                ),
                                line1: userProfile.address,
                                city: userProfile.suburb,
                                state: stateName,
                                postal_code: `${userProfile.postcode}`,
                            },
                        },
                    },
                },
            }
        );

        if (error) {
            dispatch(isCardVerifiedSet(false));
            dispatch(errorSet(error.message));
            setLoading(false);
            return;
        }

        try {
            const {data, error} = await fetchSummary({
                confirmationToken: confirmationToken.id,
                paymentIntentId,
                manufacturerId: userProfile.manufacturerId,
                paymentMethod,
                jobId: job.id,
            });

            if (error) {
                dispatch(isCardVerifiedSet(false));
                dispatch(errorSet(staticMessage));
            } else {
                dispatch(isCardVerifiedSet(true));
                dispatch(confirmationTokenSet(confirmationToken.id));
                setPaymentOptions(data.paymentOptions, job.taxRate);

                if (
                    data.paymentMethodType == CardType.card_domestic ||
                    data.paymentMethodType == CardType.card_international
                ) {
                    dispatch(cardTypeSet(data.paymentMethodType));
                }

                const updateResponse = await updateIntent(
                    paymentMethod,
                    job.id,
                    userProfile.manufacturerId
                );

                if (updateResponse?.error) {
                    dispatch(isCardVerifiedSet(false));
                    dispatch(errorSet(staticMessage));
                }
            }
        } catch (e) {
            dispatch(isCardVerifiedSet(false));
            dispatch(errorSet(staticMessage));
        } finally {
            setLoading(false);
        }
    }, [
        stripe,
        elements,
        userProfile,
        stateLabel,
        fetchSummary,
        job,
        connectedAccountId,
        paymentIntentId,
        staticMessage,
        paymentMethod,
        getCountryCode,
        getStateName,
        setPaymentOptions,
        updateIntent,
    ]);

    const formSubmitHandler = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            void handlePaymentDetails();
        },
        [handlePaymentDetails]
    );

    const changeHandler = useCallback(
        (event: StripePaymentElementChangeEvent) => {
            if (event.complete && htmlForm.current) {
                htmlForm.current.requestSubmit();
            }
        },
        []
    );

    const handleNextAction = useCallback(
        (clientSecret: string) => {
            if (stripe) {
                return stripe.handleNextAction({clientSecret});
            }
        },
        [stripe]
    );

    useEffect(() => {
        dispatch(isLoadingPaymentInfoSet(loading || isLoading));
    }, [loading, isLoading]);

    useEffect(() => {
        // if switched from deposit to full and vice versa and form is already filled
        // need to call the handlePaymentDetals();
        if (isCardVerified && paymentType == PaymentType.GATEWAY) {
            // this means card was verified in last change event
            if (htmlForm.current) {
                htmlForm.current.requestSubmit();
            }
        }
    }, [paymentType]);

    useEffect(() => {
        setExtraActionHandler({action: handleNextAction});
    }, [handleNextAction]);

    useEffect(() => {
        return () => {
            dispatch(isLoadingPaymentInfoSet(false));
            dispatch(errorsSet([]));
        };
    }, []);

    return (
        <form ref={htmlForm} onSubmit={formSubmitHandler}>
            <PaymentElement
                onChange={changeHandler}
                options={{
                    fields: {
                        billingDetails: {
                            name: 'auto',
                            address: {
                                country: 'never',
                            },
                        },
                    },
                    layout: {
                        type: 'tabs',
                        defaultCollapsed: false,
                    },
                }}
            />
        </form>
    );
};
